import { BUILDING_DEFINITIONS } from "@kaeliux/shared";
/**
 * Resolve the growth multiplier that applies when stepping up to `level`.
 *
 * Breakpoints are walked in order; the first entry whose `upToLevel` is
 * greater than or equal to the target level wins. Levels past the final
 * breakpoint reuse the last multiplier so the curve never resets.
 */
export function resolveBreakpointMultiplier(breakpoints, level) {
    for (const { upToLevel, multiplier } of breakpoints) {
        if (level <= upToLevel)
            return multiplier;
    }
    const last = breakpoints[breakpoints.length - 1];
    return last ? last.multiplier : 1;
}
/**
 * Compound `base` through a per-level breakpoint curve from level 2 up to
 * `level` (level 1 is `base` itself) — the shared "growth curve" step of the
 * base(building, level) → curve → external-multiplier pipeline, reused by
 * both Economic resource production (resources.ts) and Energy production
 * (resourceEngine.ts) so the compounding logic exists in exactly one place.
 *
 * `roundEachStep` selects which of two conventions a caller's curve uses —
 * this function doesn't unify them, it just gives each a single shared
 * implementation:
 *   - `true`: floor after every intermediate step — Energy's Solar/Nuclear
 *     contributions (matches the pre-existing live-tick formula exactly),
 *     and, since issue #103, Economic resource production (Poly-Carbon Extractor /
 *     Crystals Mine / Hydrogen Refinery) — the design numbers for those
 *     buildings were computed against a floor-every-level curve.
 *   - `false`: compound at full precision, caller rounds once at the end —
 *     everything else (e.g. Launcher population), where flooring every level
 *     would drift the result off its own verified spreadsheet values.
 */
export function growByBreakpoints(base, multipliers, level, roundEachStep) {
    let value = base;
    for (let n = 2; n <= level; n++) {
        const multiplier = resolveBreakpointMultiplier(multipliers, n);
        value = roundEachStep ? Math.floor(value * multiplier) : value * multiplier;
    }
    return value;
}
const ECONOMIC_KEYS = ["metal", "minerals", "gas"];
/** Architecture science reduces building prices this much per level… */
const ARCHITECTURE_REDUCTION_PER_LEVEL = 0.01;
/** …up to this fraction off at level 10 (see docs/game-details/sciences.md). */
const ARCHITECTURE_REDUCTION_CAP = 0.10;
/**
 * Fraction Architecture science shaves off building prices (metal /
 * minerals / gas): 1% per level, capped at 10% at level 10. Energy cost is
 * untouched — it follows its own documented curve (issue #52) and the
 * science's doc entry only calls out "price".
 */
export function calculateBuildingCostReduction(architectureLevel) {
    const level = Math.max(0, architectureLevel);
    return Math.min(ARCHITECTURE_REDUCTION_CAP, level * ARCHITECTURE_REDUCTION_PER_LEVEL);
}
/** Energy growth switches from ×1.25 to ×1.1 once the target level exceeds this. */
const ENERGY_BREAKPOINT_LEVEL = 20;
const ENERGY_MULT_EARLY = 1.25;
const ENERGY_MULT_LATE = 1.1;
/**
 * Energy cost to upgrade `buildingType` from `currentLevel` → `currentLevel + 1`.
 *
 * Formula:
 *   energy(1) = baseCost.energy
 *   energy(n) = energy(n-1) × 1.25   for n ≤ 20
 *   energy(n) = energy(n-1) × 1.1    for n >  20
 *
 * Multiplications are kept in floating point so a small base (e.g. 2) does
 * not stall under an iterative floor; the result is floored once for
 * display and consumption. Buildings whose `baseCost.energy` is 0 or
 * missing (Solar Plant, Fusion Plant) return 0.
 */
export function calculateBuildingEnergyCost(buildingType, currentLevel) {
    const building = BUILDING_DEFINITIONS[buildingType];
    if (!building) {
        throw new Error(`Unknown building type: ${buildingType}`);
    }
    const base = building.baseCost.energy ?? 0;
    if (base === 0)
        return 0;
    const targetLevel = currentLevel + 1;
    let value = base;
    for (let n = 2; n <= targetLevel; n++) {
        const mult = n <= ENERGY_BREAKPOINT_LEVEL ? ENERGY_MULT_EARLY : ENERGY_MULT_LATE;
        value = value * mult;
    }
    return Math.floor(value);
}
/**
 * Calculate the cost to upgrade a building to the next level.
 *
 * Economic resources (metal / minerals / gas) compound at full precision
 * across `costMultipliers` breakpoints and round only once — rounding at
 * every step would compound error across levels:
 *   cost(1) = baseCost
 *   cost(n) = cost(n-1) × multiplier_at_level_n   (per `costMultipliers`)
 *   result  = round(cost(level))
 *
 * Energy follows a fixed ×1.25 / ×1.1 curve — see {@link calculateBuildingEnergyCost}
 * — and is not discounted by Architecture (see {@link calculateBuildingCostReduction}).
 *
 * @param buildingType - The type of building
 * @param currentLevel - Current building level (the cost returned is for level + 1)
 * @param architectureLevel - Player's Architecture science level (0 = no discount)
 */
export function calculateBuildingUpgradeCost(buildingType, currentLevel, architectureLevel = 0) {
    const building = BUILDING_DEFINITIONS[buildingType];
    if (!building) {
        throw new Error(`Unknown building type: ${buildingType}`);
    }
    const { baseCost, costMultipliers } = building;
    if (!costMultipliers || costMultipliers.length === 0) {
        throw new Error(`Building ${buildingType} is missing costMultipliers`);
    }
    const targetLevel = currentLevel + 1;
    const discount = 1 - calculateBuildingCostReduction(architectureLevel);
    const cost = {};
    for (const key of ECONOMIC_KEYS) {
        const base = baseCost[key];
        if (base === undefined)
            continue;
        let value = base;
        for (let n = 2; n <= targetLevel; n++) {
            const multiplier = resolveBreakpointMultiplier(costMultipliers, n);
            value *= multiplier;
        }
        cost[key] = Math.round(value * discount);
    }
    cost.energy = calculateBuildingEnergyCost(buildingType, currentLevel);
    return cost;
}
// ---------------------------------------------------------------------------
// Build duration
// ---------------------------------------------------------------------------
/** The x-speed the duration curve below is calibrated for. */
export const BUILD_TIME_BASE_SPEED = 10;
/** Engineering Facility shaves this fraction off build time per level… */
export const FACTORY_TIME_REDUCTION_PER_LEVEL = 0.0375;
/** …down to at most 25% of the base time (cap hit at factory level 20). */
export const FACTORY_TIME_REDUCTION_CAP = 0.75;
/**
 * Smoothstep interpolation in log-cost space between two curve anchors:
 * cost C0 → T0 seconds, cost C1 → T1 seconds.
 */
function smoothSegment(c, c0, c1, t0, t1) {
    const u = Math.log(c / c0) / Math.log(c1 / c0);
    const s = 3 * u * u - 2 * u * u * u;
    return t0 * Math.pow(t1 / t0, s);
}
/**
 * Base build time in seconds for a total resource cost, calibrated for a
 * 10x-speed universe. Anchors: 1k→5s, 10k→1m, 100k→10m, 1M→1h, 30M→3h,
 * 100M→5h; costs beyond 100M clamp to the 5h anchor (curve undefined
 * there — revisit if such costs ever exist). The final ×0.98 keeps values
 * safely under the anchor caps.
 */
export function buildTimeSeconds10x(totalCost) {
    let t;
    if (totalCost < 1000) {
        t = Math.max(1, 5 * Math.pow(totalCost / 1000, 0.8));
    }
    else if (totalCost <= 10000) {
        t = smoothSegment(totalCost, 1000, 10000, 5, 60);
    }
    else if (totalCost <= 100000) {
        t = smoothSegment(totalCost, 10000, 100000, 60, 600);
    }
    else if (totalCost <= 1000000) {
        t = smoothSegment(totalCost, 100000, 1000000, 600, 3600);
    }
    else if (totalCost <= 30000000) {
        t = smoothSegment(totalCost, 1000000, 30000000, 3600, 10800);
    }
    else if (totalCost <= 100000000) {
        t = smoothSegment(totalCost, 30000000, 100000000, 10800, 18000);
    }
    else {
        t = 18000;
    }
    return t * 0.98;
}
/**
 * Seconds to build `buildingType` up to `targetLevel` (cost of
 * `targetLevel − 1` → `targetLevel`), scaled from the 10x-calibrated curve
 * to the universe's speed and reduced linearly by the Engineering Facility.
 */
export function computeBuildDurationSeconds(buildingType, targetLevel, robotFactoryLevel, speedMultiplier) {
    const cost = calculateBuildingUpgradeCost(buildingType, targetLevel - 1);
    const totalCost = (cost.metal || 0) +
        (cost.minerals || 0) +
        (cost.gas || 0) +
        (cost.energy || 0);
    const reduction = Math.min(FACTORY_TIME_REDUCTION_CAP, FACTORY_TIME_REDUCTION_PER_LEVEL * Math.max(0, robotFactoryLevel));
    const speed = speedMultiplier > 0 ? speedMultiplier : 1;
    const scaled = buildTimeSeconds10x(totalCost) * (BUILD_TIME_BASE_SPEED / speed) * (1 - reduction);
    return Math.max(1, Math.floor(scaled));
}
/**
 * Check if a cached `ResourceCost` is affordable given the player's current
 * resources.
 */
export function canAffordCost(cost, resources) {
    if (cost.metal !== undefined && resources.metal < cost.metal)
        return false;
    if (cost.minerals !== undefined && resources.minerals < cost.minerals)
        return false;
    if (cost.gas !== undefined && resources.gas < cost.gas)
        return false;
    // Energy: only check when the cost is positive. Solar / Fusion plants
    // carry `cost.energy = 0` and must remain buildable even when the
    // planet's `resources.energy` has gone negative (consumed > income) —
    // that is exactly the case where the player needs to build them.
    if (cost.energy !== undefined && cost.energy > 0 && resources.energy < cost.energy)
        return false;
    return true;
}
/**
 * Check whether a tagged-union requirement array (`{ type: 'building' |
 * 'science', id, level }[]`) is satisfied. Shared by building prerequisites
 * (`checkPrerequisites`) and science requirements (`checkScienceRequirements`
 * in `sciences.ts`) since both use the same requirement shape. Missing
 * levels (building or science) are treated as 0.
 */
export function meetsRequirements(requirements, buildingLevels, scienceLevels = {}) {
    for (const req of requirements) {
        if (req.type === "building") {
            const current = buildingLevels[req.id] ?? 0;
            if (current < req.level)
                return false;
        }
        else {
            const current = scienceLevels[req.id] ?? 0;
            if (current < req.level)
                return false;
        }
    }
    return true;
}
/**
 * Same tagged-union requirements array {@link meetsRequirements} checks, but
 * returns the specific unmet entries (with the player's current level for
 * contrast) instead of a single boolean. Shared by the Sciences and Zone
 * pages so they can render "Requires: Research Center L5 (L2), Physics L4 (L0)"
 * instead of a generic "Prerequisites not met" string. Missing levels
 * (building or science) are treated as 0, same as `meetsRequirements`.
 */
export function getUnmetRequirements(requirements, buildingLevels, scienceLevels = {}) {
    const unmet = [];
    for (const req of requirements) {
        if (req.type === "building") {
            const currentLevel = buildingLevels[req.id] ?? 0;
            if (currentLevel < req.level) {
                unmet.push({ type: "building", id: req.id, requiredLevel: req.level, currentLevel });
            }
        }
        else {
            const currentLevel = scienceLevels[req.id] ?? 0;
            if (currentLevel < req.level) {
                unmet.push({ type: "science", id: req.id, requiredLevel: req.level, currentLevel });
            }
        }
    }
    return unmet;
}
/**
 * Check if building prerequisites are met.
 *
 * Requirements are a tagged-union array of `{ type: 'building' | 'science', id, level }`.
 * Missing science levels are treated as 0.
 */
export function checkPrerequisites(buildingType, buildingLevels, scienceLevels = {}) {
    const building = BUILDING_DEFINITIONS[buildingType];
    if (!building || !building.requirements || building.requirements.length === 0) {
        return true;
    }
    return meetsRequirements(building.requirements, buildingLevels, scienceLevels);
}
/**
 * Get the maximum level for a building.
 */
export function getMaxBuildingLevel(buildingType) {
    const building = BUILDING_DEFINITIONS[buildingType];
    return building?.maxLevel ?? 0;
}
/**
 * Check if a building can be upgraded (max level + prerequisites).
 *
 * Note: Energy affordability is enforced at the resource-cost layer via
 * {@link canAffordCost}, which checks `resources.energy` against
 * `nextCost.energy`. When planet energy is negative (consumption > income),
 * only Solar / Nuclear plants — whose `nextCost.energy` is 0 — remain
 * affordable.
 */
export function canUpgradeBuilding(buildingType, currentLevel, buildingLevels, scienceLevels = {}) {
    const maxLevel = getMaxBuildingLevel(buildingType);
    if (maxLevel > 0 && currentLevel >= maxLevel)
        return false;
    if (!checkPrerequisites(buildingType, buildingLevels, scienceLevels))
        return false;
    return true;
}
/**
 * Convenience: extract a `Record<BuildingType, number>` of levels from a
 * `Record<BuildingType, PlanetBuilding>` (planet doc shape).
 */
export function extractBuildingLevels(buildings) {
    const result = {};
    for (const key of Object.keys(BUILDING_DEFINITIONS)) {
        result[key] = buildings[key]?.level ?? 0;
    }
    return result;
}
/**
 * Convenience: convert a `PlayerSciences` doc into a `Record<ScienceId, number>`.
 */
export function extractScienceLevels(sciences) {
    if (!sciences)
        return {};
    return sciences;
}
//# sourceMappingURL=buildings.js.map